<script setup lang="ts">
  import { computed, useId } from 'vue';
  import { FontIcon } from '../FontIcon';
  import {
    baseClass,
    dotClass,
    emphasisClasses,
    iconClass,
    iconOnlyBaseClass,
    textClass,
    toneClasses,
  } from './Badge.css';
  import type { BadgeProps } from './Badge.types';
  import { BadgeEmphasis, BadgeVariant } from './Badge.types';

  const props = withDefaults(defineProps<BadgeProps>(), {
    variant: BadgeVariant.TEXT,
    emphasis: BadgeEmphasis.PRIMARY,
  });

  defineSlots<{
    icon?: () => unknown;
  }>();

  const uid = useId();
  const resolvedTestId = computed(() => props.testId ?? `ds-badge-${uid}`);

  const iconOnlyVersion = computed(
    () => props.variant === BadgeVariant.ICON_ONLY,
  );

  const classes = computed(() => {
    if (iconOnlyVersion.value) {
      return [iconOnlyBaseClass, toneClasses[props.color]];
    }

    return [
      baseClass,
      toneClasses[props.color],
      emphasisClasses[props.emphasis],
    ];
  });
</script>

<template>
  <span :class="classes" :data-testid="resolvedTestId">
    <span v-if="iconOnlyVersion" :class="iconClass" aria-hidden="true">
      <slot name="icon">
        <FontIcon v-if="iconName" :icon-name="iconName" />
      </slot>
    </span>
    <template v-else>
      <span :class="dotClass" aria-hidden="true" />
      <span
        :class="textClass"
        :title="text"
        :data-testid="`${resolvedTestId}-text`"
        >{{
          text
        }}</span
      >
    </template>
  </span>
</template>
